"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Select = Select;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const lucide_react_1 = require("lucide-react");
const cn_1 = require("../lib/cn");
const folded_1 = require("../lib/folded");
const overlay_1 = require("../lib/overlay");
const icon_1 = require("./icon");
const truncated_text_1 = require("./truncated-text");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The app's dropdown. A themed listbox over a real <select>.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS WAS THE NATIVE CONTROL UNTIL 9 Sep 2026, DELIBERATELY, AND THE REVERSAL IS THE
 * PRODUCT OWNER'S.** The note that stood here argued the native `<select>` on its merits — the
 * platform picker on a phone, keyboard behaviour, type-ahead and assistive-technology support that
 * a custom listbox has to reimplement and usually reimplements incompletely. Every word of that is
 * still true and is why the work below exists rather than a `<div>` with a click handler.
 *
 * ⚠️⚠️ **WHAT IT COULD NOT ANSWER IS THAT THE POPUP IS DRAWN BY THE OPERATING SYSTEM.** A native
 * select's menu takes no CSS at all: not the app's radius, not its ink, not its dark theme. The
 * tester feedback is a list of controls that look like the app until you click them, at which point
 * they look like Windows — and no amount of styling the closed state fixes that. `qol-fixes.md` D2.
 *
 * ⚠️⚠️ **EVERY CALL SITE IS UNTOUCHED, WHICH WAS D2's CONDITION.** `value`, `options` and
 * `onChange(event)` are what 65 call sites pass, and `event.target.value` is the only thing any of
 * them reads — checked across the whole app before this was written. So the shape of the event is
 * preserved and the callers do not move.
 *
 * ⚠️⚠️ **AND THE EVENT IS BUILT RATHER THAN EMITTED BY A HIDDEN `<select>`, WHICH IS THE SECOND
 * DESIGN AND THE RIGHT ONE.** The first kept a real element mounted and `aria-hidden`, dispatching
 * a genuine `change` through the prototype's value setter — pleasingly honest, and wrong: `Field`
 * wraps its children in a `<label>`, so the hidden control was labelled by it too. That is two form
 * controls answering to one name. The test suite found it immediately — *"found multiple elements
 * with the text of /Projekt/"* — and a screen reader would have found the same thing, which is the
 * part that matters. `aria-hidden` does not undo an implicit label association.
 *
 * ⚠️ **So there is exactly one control here**, and the cast below is the price. It is contained to
 * one line, it is only ever handed `{ target, currentTarget }`, and the alternative was a duplicate
 * control in the accessibility tree of every form in the app.
 *
 * ⚠️ **The button is the control.** It is a `combobox` with `aria-expanded` and `aria-controls`; the
 * list is a `listbox` of `option`s with `aria-selected`. The label and the id ride on it, because
 * that is what a person reaches and what a label points at.
 */
function Select({ options, className, searchable = false, searchPlaceholder, menuWidth, emptyLabel, action, inline = false, triggerLabel, icon, ...rest }) {
    const { id, disabled, value, defaultValue, onChange, ...selectProps } = rest;
    /**
     * ⚠️ **Data attributes belong on the BUTTON**, because that is the element a test clicks and an
     * analytics hook sees. Spreading them onto a hidden select would put `data-action` on a node
     * nobody can reach.
     */
    const passThrough = {};
    for (const [key, entry] of Object.entries(selectProps)) {
        if (key.startsWith('data-') || key.startsWith('aria-'))
            passThrough[key] = entry;
    }
    const listId = (0, react_1.useId)();
    /**
     * ⚠️ **Uncontrolled is supported because the old component supported it**, and one call site
     * without a `value` would otherwise render a permanently empty trigger. The native element stays
     * the source of truth; this only mirrors it so the trigger has something to draw.
     */
    const [mirror, setMirror] = (0, react_1.useState)(() => {
        if (value !== undefined)
            return String(value);
        if (defaultValue !== undefined)
            return String(defaultValue);
        return options[0]?.value ?? '';
    });
    const current = value === undefined ? mirror : String(value);
    const [open, setOpen] = (0, react_1.useState)(false);
    const [query, setQuery] = (0, react_1.useState)('');
    const [active, setActive] = (0, react_1.useState)(0);
    const close = (0, react_1.useCallback)(() => {
        setOpen(false);
        setQuery('');
    }, []);
    const { surface, trigger } = (0, overlay_1.useDismiss)(open, close);
    const { rect } = (0, overlay_1.useAnchorRect)(open, trigger);
    const shown = (0, react_1.useMemo)(() => {
        const needle = (0, folded_1.folded)(query.trim());
        if (!searchable || needle === '')
            return options;
        return options.filter((option) => (0, folded_1.folded)(option.label).includes(needle));
    }, [options, query, searchable]);
    const selected = options.find((option) => option.value === current);
    /* ⚠️ Re-aimed at the current value each time it opens, so arrowing starts from where you are. */
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        const at = shown.findIndex((option) => option.value === current);
        setActive(at < 0 ? 0 : at);
    }, [open, current, shown]);
    const pick = (0, react_1.useCallback)((option) => {
        if (option.disabled === true)
            return;
        close();
        setMirror(option.value);
        /**
         * ⚠️ **The one cast in the file, and the header says why it is here.** Callers read
         * `event.target.value` and nothing else; `currentTarget` is set to the same object so a
         * caller reaching for either finds the value rather than `undefined`.
         */
        const target = { value: option.value };
        onChange?.({ target, currentTarget: target });
    }, [close, onChange]);
    const move = (delta) => {
        if (shown.length === 0)
            return;
        setActive((at) => {
            let next = at;
            /* ⚠️ One full lap at most: a list where every option is disabled must not spin for ever. */
            for (const _ of shown) {
                next = (next + delta + shown.length) % shown.length;
                if (shown[next]?.disabled !== true)
                    return next;
            }
            return at;
        });
    };
    const onTriggerKey = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
            event.preventDefault();
            setOpen(true);
            return;
        }
        if (event.key === ' ') {
            event.preventDefault();
            setOpen(true);
        }
    };
    const onListKey = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            move(1);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            move(-1);
        }
        else if (event.key === 'Home') {
            event.preventDefault();
            setActive(0);
        }
        else if (event.key === 'End') {
            event.preventDefault();
            setActive(shown.length - 1);
        }
        else if (event.key === 'Enter') {
            event.preventDefault();
            const option = shown[active];
            if (option !== undefined)
                pick(option);
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            close();
            trigger.current?.querySelector('button')?.focus();
        }
    };
    /* ⚠️ The caller's `className` goes on the BUTTON, not on this wrapper: the button is the control,
       and a width or a height set out here would size a box the button then ignores. */
    return (
    /**
     * ⚠️⚠️ **`className` LANDS ON THE BUTTON, AND THIS WRAPPER IS ALWAYS `w-full`. A CALLER SIZES A
     * SELECT BY WRAPPING IT, NEVER BY PASSING `w-[…]`.** Passing a width class sizes the button
     * inside a wrapper that goes on demanding a hundred percent of its container — which in a flex
     * row makes every select claim the whole row. Three settings filters stacked one per line
     * because of it, and when the row was stopped from wrapping the same demand opened a gap the
     * width of the card between them. The fix at the call site is
     * `<span className="w-[150px] shrink-0"><Select … /></span>`, which is what `TextInput` gets
     * already.
     *
     * ⚠️ **`w-full` stays**, because the common case is a select filling a form field and every
     * such call site relies on it. Moving the caller's class here instead would silently change the
     * width of every one of them.
     */
    (0, jsx_runtime_1.jsxs)("div", { ref: trigger, className: "relative inline-flex w-full min-w-0", children: [(0, jsx_runtime_1.jsxs)("button", { type: "button", id: id, role: "combobox", "data-value": current, "aria-expanded": open, "aria-controls": open ? listId : undefined, "aria-haspopup": "listbox", disabled: disabled, onClick: () => {
                    setOpen((was) => !was);
                }, onKeyDown: onTriggerKey, className: (0, cn_1.cn)('focus-visible:border-ring focus-visible:ring-ring/50 flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md border text-left transition-[color,background-color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50', inline
                    ? 'hover:border-input hover:bg-background h-[30px] border-transparent bg-transparent px-2 py-1 text-[12.5px] shadow-none'
                    : 'border-input h-8 bg-transparent px-2.5 py-1 text-sm shadow-xs', className), ...passThrough, children: [selected?.media !== undefined ? ((0, jsx_runtime_1.jsx)("span", { className: "flex shrink-0 items-center", children: selected.media })) : (selected?.icon ?? icon) === undefined ? null : ((0, jsx_runtime_1.jsx)(icon_1.Icon, { name: selected?.icon ?? icon ?? 'circle', size: 14, className: "text-muted-foreground shrink-0" })), (0, jsx_runtime_1.jsx)(truncated_text_1.TruncatedText, { className: "flex-1", tooltip: selected?.label, children: triggerLabel ?? selected?.label ?? '' }), triggerLabel === undefined ? ((0, jsx_runtime_1.jsx)(lucide_react_1.ChevronDown, { size: 14, "aria-hidden": "true", className: "shrink-0 opacity-50" })) : null] }), open
                ? (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { ref: surface, 
                    /**
                     * ⚠️⚠️ **A FLOOR ON THE MENU'S WIDTH, BECAUSE A TRIGGER CAN BE TINY.** The menu used
                     * to be exactly as wide as its trigger, which is right for a form field and absurd
                     * for the pager's page-jump: a 36px ellipsis produced a 36px menu — a column of
                     * single digits under a search box narrower than the word it holds. Reported with a
                     * screenshot of exactly that.
                     *
                     * ⚠️ **A floor rather than a fixed width**, so every ordinary `Select` still matches
                     * its field to the pixel and only the small triggers grow.
                     */
                    style: (0, overlay_1.anchorTo)(rect, { width: Math.max(rect?.width ?? 220, menuWidth ?? 176) }), className: "pop-in bg-popover text-popover-foreground shadow-pop overflow-hidden rounded-md border", children: [searchable ? ((0, jsx_runtime_1.jsx)("div", { className: "border-b p-1", children: (0, jsx_runtime_1.jsx)("input", { autoFocus: true, value: query, placeholder: searchPlaceholder, "aria-label": searchPlaceholder, onChange: (event) => {
                                    setQuery(event.target.value);
                                    setActive(0);
                                }, onKeyDown: onListKey, className: "h-7 w-full bg-transparent px-2 text-sm outline-none" }) })) : null, (0, jsx_runtime_1.jsx)("div", { id: listId, role: "listbox", tabIndex: searchable ? -1 : 0, autoFocus: !searchable, onKeyDown: onListKey, className: "max-h-60 overflow-y-auto p-1 outline-none", children: shown.length === 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground px-2 py-1.5 text-sm", children: emptyLabel })) : (shown.map((option, at) => ((0, jsx_runtime_1.jsxs)("button", { type: "button", role: "option", "data-value": option.value, "aria-selected": option.value === current, disabled: option.disabled === true, onMouseEnter: () => {
                                    setActive(at);
                                }, onClick: () => {
                                    pick(option);
                                }, className: (0, cn_1.cn)('flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none', at === active ? 'bg-accent text-accent-foreground' : '', option.disabled === true ? 'cursor-not-allowed opacity-50' : ''), children: [option.media !== undefined ? ((0, jsx_runtime_1.jsx)("span", { className: "flex shrink-0 items-center", children: option.media })) : option.icon === undefined ? null : ((0, jsx_runtime_1.jsx)(icon_1.Icon, { name: option.icon, size: 14, className: "text-muted-foreground shrink-0" })), (0, jsx_runtime_1.jsx)(truncated_text_1.TruncatedText, { className: "flex-1", children: option.label }), option.hint === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground shrink-0 text-[11.5px] tabular-nums", children: option.hint })), option.value === current ? ((0, jsx_runtime_1.jsx)(lucide_react_1.Check, { size: 14, "aria-hidden": "true", className: "shrink-0" })) : null] }, option.value)))) }), action === undefined ? null : ((0, jsx_runtime_1.jsx)("div", { className: "border-t p-1", children: (0, jsx_runtime_1.jsxs)("button", { type: "button", className: "hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none", onClick: () => {
                                    close();
                                    action.onSelect();
                                }, children: [(0, jsx_runtime_1.jsx)(lucide_react_1.Plus, { size: 14, "aria-hidden": "true", className: "shrink-0" }), (0, jsx_runtime_1.jsx)("span", { className: "min-w-0 flex-1 truncate", children: action.label })] }) }))] }), document.body)
                : null] }));
}
//# sourceMappingURL=select.js.map